import {
  EXP_CATEGORY_DAY_TRIPS_ID,
  EXP_CATEGORY_FOOD_AND_DRINK_ID,
  EXP_CATEGORY_GUIDED_TOURS_ID,
  EXP_CATEGORY_SHOWS_AND_EVENTS_ID,
  EXP_CATEGORY_TICKETS_AND_PASSES_ID,
  EXP_CATEGORY_TOP_ACTIVITIES_ID,
  EXP_CATEGORY_WELLNESS_AND_SPA_ID,
} from 'constants/experience'

/**
 * Global display priority for experience parent categories.
 * Used when the searched place has no city-specific ranking configured.
 */
export const GLOBAL_CATEGORY_PRIORITY: ReadonlyArray<number> = [
  EXP_CATEGORY_TOP_ACTIVITIES_ID,
  EXP_CATEGORY_DAY_TRIPS_ID,
  EXP_CATEGORY_TICKETS_AND_PASSES_ID,
  EXP_CATEGORY_FOOD_AND_DRINK_ID,
  EXP_CATEGORY_GUIDED_TOURS_ID,
  EXP_CATEGORY_WELLNESS_AND_SPA_ID,
  EXP_CATEGORY_SHOWS_AND_EVENTS_ID,
]

/**
 * City-specific category rankings keyed by LE place id.
 * Each entry is an ordered list of parent category ids, most relevant first.
 * Categories not listed for a city fall back to the global priority.
 *
 * Intentionally empty for V1. The current product-approved behaviour is the
 * global priority plus city-specific category hiding; populate this only when
 * product provides place-specific ordering overrides.
 */
export const CITY_CATEGORY_RANKINGS: Readonly<
  Record<string, ReadonlyArray<number>>
> = {}

export interface CategoryRelevanceConfig {
  cityRankings: Readonly<Record<string, ReadonlyArray<number>>>
  globalPriority: ReadonlyArray<number>
}

const DEFAULT_CONFIG: CategoryRelevanceConfig = {
  cityRankings: CITY_CATEGORY_RANKINGS,
  globalPriority: GLOBAL_CATEGORY_PRIORITY,
}

export interface RankableCategory {
  categoryId: number
  name: string
  count: number
}

const UNRANKED = Number.MAX_SAFE_INTEGER

interface RelevanceRank {
  cityRank: number
  globalRank: number
}

function getRelevanceRank(
  place: App.Place | undefined,
  categoryId: number,
  config: CategoryRelevanceConfig,
): RelevanceRank {
  let cityRank = UNRANKED
  if (place?.id) {
    const cityRanking = config.cityRankings[place.id]
    const index = cityRanking?.indexOf(categoryId) ?? -1
    if (index >= 0) {
      cityRank = index
    }
  }

  const globalIndex = config.globalPriority.indexOf(categoryId)

  return {
    cityRank,
    globalRank: globalIndex >= 0 ? globalIndex : UNRANKED,
  }
}

/**
 * Sorts eligible category facets by deterministic city relevance:
 * 1. City-specific ranking, when configured for the searched place
 * 2. Global category priority
 * 3. Offer count (descending) as tie-breaker only
 * 4. Category display name as final stable tie-breaker
 */
export function sortCategoriesByRelevance<T extends RankableCategory>(
  place: App.Place | undefined,
  items: Array<T>,
  config: CategoryRelevanceConfig = DEFAULT_CONFIG,
): Array<T> {
  return [...items].sort((a, b) => {
    const rankA = getRelevanceRank(place, a.categoryId, config)
    const rankB = getRelevanceRank(place, b.categoryId, config)

    if (rankA.cityRank !== rankB.cityRank) {
      return rankA.cityRank - rankB.cityRank
    }
    if (rankA.globalRank !== rankB.globalRank) {
      return rankA.globalRank - rankB.globalRank
    }
    if (a.count !== b.count) {
      return b.count - a.count
    }
    return a.name.localeCompare(b.name)
  })
}
